import { MediaItemSizeEnum, type ZnglyVideo } from '@/graphql/types';
import { Box, ListItem, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { getImageBackgroundColor } from '@/Components/posts/utils';
import { getCatalogMediaFeaturedImage, getDescription } from '@/Components/posts/utils/catalog';
import type { RenderIfVisibleProps } from '@/Utility/RenderIfVisible';
import { useAsyncMemo } from '@/Utility/hooks';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faImages, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useMemo } from 'react';
import { onSelectPlayerNode } from './catalog.utils';

export const SORTABLE_LIST_ITEM_DND_PREFIX = 'sortable-item--list-';

type Props = {
  className?: string;
  node: ZnglyVideo;
  disabled?: boolean;
  isFallback?: boolean;
  idx: number;
  onRemove?: () => void;
};

const NormHeight = 120;

const ListViewItem = ({
  node,
  disabled,
  isFallback,
  idx,
  ...props
}: Props & { sortableProps?: ReturnType<typeof useSortable> }) => {
  const featuredImage = useMemo(
    () => getCatalogMediaFeaturedImage(node?.znglyContent, MediaItemSizeEnum.Medium),
    [node?.id]
  );

  const backgroundColor = useAsyncMemo(async () => {
    return await getImageBackgroundColor(featuredImage);
  }, [featuredImage, node?.id]);

  const { aiExtractiveAnswer, description, hasAiResult } = getDescription(node);
  const desc = useMemo(
    () => (!!aiExtractiveAnswer ? aiExtractiveAnswer : description),
    [aiExtractiveAnswer, description]
  );

  const sortableProps = useMemo(() => {
    if (!props?.sortableProps) {
      return {};
    }

    const { transform, transition, isDragging } = props.sortableProps;

    return {
      ref: props?.sortableProps?.setNodeRef,
      ...props?.sortableProps?.listeners,
      ...props?.sortableProps?.attributes,
      style: {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : undefined,
      },
    };
  }, [props?.sortableProps]);

  return (
    <ListItem
      className={`Item ${disabled ? 'disabled' : ''} ${props?.className ?? ''}`}
      disablePadding
      style={{ height: `${NormHeight}px` }}
      onClick={() => {
        onSelectPlayerNode(node, idx);
      }}
      {...sortableProps}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ height: '100%', width: '100%', overflow: 'hidden' }}
        pl={1}
        alignContent="center"
        alignItems="center"
      >
        {!!props?.sortableProps && (
          <Box
            sx={{
              cursor: 'grab',
              display: 'flex',
              opacity: 0.6,
              '&:hover': {
                opacity: 1,
              },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box
          className="ImageGrid"
          sx={{
            height: `${NormHeight - 15}px`,
            backgroundColor: backgroundColor ?? '#d2d2d2',
          }}
        >
          {isFallback ? (
            <Skeleton variant="rectangular" className="img-fallback" />
          ) : (
            <img src={featuredImage ?? ''} alt={node?.title ?? ''} />
          )}
        </Box>
        <Stack pr={0}>
          <Stack direction="column" alignItems="start">
            <Tooltip title={disabled ? '' : node?.title} placement="top-end" enterDelay={500}>
              <Typography
                component="div"
                variant="body2"
                fontWeight={600}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {node?.title}
              </Typography>
            </Tooltip>
            <Typography
              component="div"
              variant="caption"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {desc}
            </Typography>
            <Stack className="Icons" flexDirection="row" alignItems="flex-end" spacing={1}>
              {hasAiResult && (
                <span>
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                </span>
              )}
              <span>
                <FontAwesomeIcon icon={faImages} /> {node?.znglyContent?.contentMediaName}
              </span>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {/* show an x button if we have a remove function */}
      {!!props?.onRemove && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            cursor: 'pointer',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '2px',
            borderRadius: '0 0 0 0.5em',
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
          onClick={props?.onRemove}
        >
          <CloseIcon />
        </Box>
      )}
    </ListItem>
  );
};

const WithSortable = (props: Props) => {
  const sortableProps = useSortable({
    id: SORTABLE_LIST_ITEM_DND_PREFIX + props?.node?.id,
    data: {
      node: props?.node,
    },
  });
  return <ListViewItem {...props} sortableProps={sortableProps} />;
};

const ListViewItemWrapper = (props: Props & { useSortable?: boolean }) => {
  if (props?.useSortable === true) {
    return <WithSortable {...props} />;
  }

  return <ListViewItem {...props} />;
};

// list render if visible props
export const listRIVProps = (node: ZnglyVideo, idx: number): Partial<RenderIfVisibleProps> => ({
  // initialVisible: true,
  stayRendered: true,
  defaultHeight: NormHeight,
  fallback: <ListViewItem key={`rif_fallback_${node?.id}`} node={node} idx={idx} isFallback />,
});

export default ListViewItemWrapper;
